import React, { useEffect, useState } from 'react';
import ReactApexChart from 'react-apexcharts';
import { chartColors } from '../../constants/layout';

function calculateTickAmount(categories) {
	const length = categories.length;
	return length <= 2 ? 2 : Math.min(length - 2, length);
}

function getMaxValue(series) {
	if (!Array.isArray(series) || series.length === 0) return null;
	let maxValues = series.map(serie => {
		if (!serie || !Array.isArray(serie.data) || serie.data.length === 0)
			return -Infinity;
		return Math.max(...serie.data.filter(num => typeof num === 'number'));
	});
	return Math.max(...maxValues) === -Infinity ? null : Math.max(...maxValues);
}

function formatNumber(value, decimal) {
	return value.toLocaleString('es-ES', {
		minimumFractionDigits: decimal,
		maximumFractionDigits: decimal,
	});
}

function measureTextWidth(text, font = '10px Arial') {
	const canvas = document.createElement('canvas');
	const context = canvas.getContext('2d');
	context.font = font;
	return context.measureText(text).width;
}

function splitTextIntoLines(text, maxWidth, font = '10px Arial') {
	if (Array.isArray(text)) return text;
	const words = text.split(' ');
	let lines = [],
		currentLine = words[0];

	for (let i = 1; i < words.length; i++) {
		const word = words[i];
		const width = measureTextWidth(currentLine + ' ' + word, font);
		if (width < maxWidth) {
			currentLine += ' ' + word;
		} else {
			lines.push(currentLine);
			currentLine = word;
		}
	}
	lines.push(currentLine);
	return lines;
}

const BarChart = React.memo(
	({
		seriesChart,
		categoriesChart,
		colorsChart,
		height,
		minHeight,
		maxHeight,
		toolbarChart = false,
		legendPosition,
		legendOffsetY = 10,
		strokeCurve = 'smooth',
		chartType,
		horizontal = false,
		um = '',
		xAxisTitle = '',
		yAxisTitle = '',
		yaxisShow = true,
		tooltipFormatter,
		decimal = 0,
		dataLabels = true,
	}) => {
		useEffect(() => {
			const tooltips = document.querySelectorAll(
				'.apexcharts-tooltip-title'
			);
			tooltips.forEach((tooltip, index) => {
				tooltip.setAttribute('data-index', index);
			});
		}, [categoriesChart]);

		const maxValue = getMaxValue(seriesChart);
		const maxYAxis = maxValue ? Math.ceil(maxValue * 1.2) : undefined;
		const unitMeasure =
			um || (seriesChart.length > 0 ? seriesChart[0].unit : 'N/A');

		const [chartHeight, setChartHeight] = useState(minHeight);

		const updateChartHeight = () => {
			const viewportHeight = window.innerHeight;
			const zoomFactor = window.devicePixelRatio || 1;
			let calculatedHeight = (viewportHeight * 0.6) / zoomFactor;

			if (calculatedHeight < minHeight) calculatedHeight = minHeight;
			else if (calculatedHeight > maxHeight) calculatedHeight = maxHeight;

			setChartHeight(calculatedHeight);
		};

		useEffect(() => {
			updateChartHeight();
			window.addEventListener('resize', updateChartHeight);
			return () => window.removeEventListener('resize', updateChartHeight);
		}, []);

		const isAreaOrLine = chartType === 'area';

		const options = {
			chart: {
				type: chartType === 'stacked' ? 'bar' : chartType,
				height: chartHeight,
				stacked: chartType === 'stacked',
				stackType: chartType === 'stacked' ? '100%' : undefined,
				toolbar: {
					show: chartType !== 'area' ? toolbarChart : false,
					tools: { download: false },
				},
				locales: [
					{
						name: 'es',
						options: {
							months: [
								'Enero',
								'Febrero',
								'Marzo',
								'Abril',
								'Mayo',
								'Junio',
								'Julio',
								'Agosto',
								'Septiembre',
								'Octubre',
								'Noviembre',
								'Diciembre',
							],
							shortMonths: [
								'Ene',
								'Feb',
								'Mar',
								'Abr',
								'May',
								'Jun',
								'Jul',
								'Ago',
								'Sep',
								'Oct',
								'Nov',
								'Dic',
							],
							days: [
								'Domingo',
								'Lunes',
								'Martes',
								'Miércoles',
								'Jueves',
								'Viernes',
								'Sábado',
							],
							shortDays: [
								'Dom',
								'Lun',
								'Mar',
								'Mié',
								'Jue',
								'Vie',
								'Sáb',
							],
							toolbar: {
								exportToSVG: 'Descargar SVG',
								exportToPNG: 'Descargar PNG',
								exportToCSV: 'Descargar CSV',
								menu: 'Menú',
							},
						},
					},
				],
				defaultLocale: 'es',
			},
			colors: colorsChart || chartColors.default,
			plotOptions: {
				bar: {
					horizontal: horizontal,
					columnWidth: '50%',
					endingShape: 'flat',
					borderRadius: 4,
					borderRadiusApplication: 'end',
					dataLabels: { position: 'top' },
				},
			},
			dataLabels: {
				enabled: dataLabels,
				formatter: val => formatNumber(val, decimal),
				offsetY: -20,
				style: { fontSize: '10px', colors: ['#adb5bd'] },
			},
			stroke: {
				show: true,
				width: isAreaOrLine ? 2 : 1,
				curve: strokeCurve,
				colors: colorsChart || chartColors.default,
			},
			xaxis: {
				categories: categoriesChart.map(cat =>
					splitTextIntoLines(cat, 100)
				),
				labels: {
					show: true,
					rotate: -30,
					maxHeight: 90,
					style: { fontSize: '10px', whiteSpace: 'normal' },
				},
				tickPlacement: isAreaOrLine ? 'on' : 'between',
				forceNiceScale: true,
				title: { text: xAxisTitle, style: { fontSize: '0.75rem' } },
			},
			yaxis: {
				show: yaxisShow,
				max: maxYAxis,
				labels: {
					formatter: val =>
						typeof val !== 'number' || isNaN(val)
							? val
							: val >= 1000
							? Math.round(val / 100) / 10 + 'k'
							: Math.round(val * 10) / 10,
				},
				title: { text: yAxisTitle, style: { fontSize: '0.75rem' } },
			},
			fill: { opacity: 1 },
			tooltip: {
				enabled: true,
				custom: tooltipFormatter
					? ({ series, seriesIndex, dataPointIndex, w }) => {
							if (!w || !w.globals || !w.config) return ''; // ← validación segura
							return tooltipFormatter({
								series,
								seriesIndex,
								dataPointIndex,
								w,
							});
					  }
					: undefined,
				style: { fontSize: '0.688rem' },
			},
			legend: { position: legendPosition, offsetY: legendOffsetY },
		};

		return (
			<ReactApexChart
				dir='ltr'
				options={options}
				series={seriesChart}
				type={chartType}
				height={chartHeight}
				className='apex-charts'
			/>
		);
	}
);

export default BarChart;
